import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { Option } from 'src/app/components/select/select.component';
import {
  ACCESS_REASON_MAX_LENGTH,
  ACCESS_ROLES,
  ACCESS_SCOPE_MAX_VALUES,
  AccessCase,
  AccessConstraint,
  AccessRole,
  SessionAccess,
  accessConstraintName
} from 'src/app/models/organizationAccess';
import { CaseService } from 'src/app/services/case.service';
import { OrganizationAccessService } from 'src/app/services/organization-access.service';
import { UtilsService } from 'src/app/services/utils.service';

export interface OrganizationAccessDialogData {
  /** The client organisation entered, fixed. */
  organization: { id: string; name: string };
  /** The access in progress, when changing role from inside it: prefills the form. */
  access?: SessionAccess;
  /** Where the application reloads once inside, instead of the client's home. */
  returnUrl?: string;
}

/**
 * Enter a client organisation with a role (organisation access), or change role from inside one. On success
 * the session is replaced and the page reloads at the client's home, or at `returnUrl`: the dialog never
 * closes by itself then.
 */
@Component({
  selector: 'app-organization-access-dialog',
  templateUrl: './organization-access-dialog.component.html',
  styleUrls: ['./organization-access-dialog.component.scss']
})
export class OrganizationAccessDialogComponent implements OnInit {
  readonly organization: { id: string; name: string };
  readonly changing: boolean;

  readonly REASON_MAX_LENGTH = ACCESS_REASON_MAX_LENGTH;
  readonly SCOPE_MAX_VALUES = ACCESS_SCOPE_MAX_VALUES;

  readonly roles: Option[] = ACCESS_ROLES.map((role) => ({ label: `USER_ROLES.${role}`, value: role }));
  role?: AccessRole = 'ADMIN';
  reason?: string;
  reasonError: { value: boolean, message?: string } = { value: false, message: undefined };

  /** Case Operator scope: the cases picked, and the ones the search shows. */
  selectedCases: AccessCase[] = [];
  cases: AccessCase[] = [];
  caseQuery?: string;
  loadingCases = false;
  private casesLoaded = false;
  private casesRequest = 0;
  private readonly caseQuery$ = new Subject<string | undefined>();

  /** Country Operator scope: territory codes. */
  territories: string[] = [];

  submitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OrganizationAccessDialogData,
    private caseService: CaseService,
    private destroyRef: DestroyRef,
    private dialogRef: MatDialogRef<OrganizationAccessDialogComponent>,
    private organizationAccessService: OrganizationAccessService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private utils: UtilsService
  ) {
    this.organization = data.organization;
    this.changing = !!data.access;
    this.caseQuery$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((query) => this.searchCases(query));
  }

  ngOnInit(): void {
    const access = this.data.access;
    if (!access) {
      return;
    }
    this.role = access.role;
    this.reason = access.reason;
    if (access.constraint?.name === 'COUNTRY') {
      this.territories = [...access.constraint.values];
    }
    if (access.constraint?.name === 'CASE') {
      void this.prefillCases(access.constraint.values);
    } else if (this.role === 'CASE_OPERATOR') {
      void this.searchCases();
    }
  }

  onRoleChange(role: AccessRole | undefined) {
    this.role = role;
    if (role === 'CASE_OPERATOR' && !this.casesLoaded) {
      void this.searchCases();
    }
  }

  onCaseQueryChange() {
    this.caseQuery$.next(this.caseQuery);
  }

  isCaseSelected(option: AccessCase) {
    return this.selectedCases.some((c) => c._id === option._id);
  }

  toggleCase(option: AccessCase) {
    if (this.isCaseSelected(option)) {
      this.selectedCases = this.selectedCases.filter((c) => c._id !== option._id);
    } else if (this.selectedCases.length < this.SCOPE_MAX_VALUES) {
      this.selectedCases = [...this.selectedCases, option];
    }
  }

  caseLabel(option: AccessCase) {
    return option.identifier || option.reference;
  }

  onTerritoriesChange(values: string[] | undefined) {
    this.territories = values ?? [];
  }

  onReasonChange(reason: string | undefined) {
    this.reason = reason ?? undefined;
    const tooLong = (this.reason?.length ?? 0) > this.REASON_MAX_LENGTH;
    this.reasonError = {
      value: tooLong,
      message: tooLong ? this.translate.instant('ACCESS.DIALOG.REASON_TOO_LONG', { max: this.REASON_MAX_LENGTH }) : undefined
    };
  }

  /** The scope the chosen role requires, or undefined when it takes none. */
  private constraint(): AccessConstraint | undefined {
    const name = this.role ? accessConstraintName(this.role) : undefined;
    if (name === 'CASE') {
      return { name, values: this.selectedCases.map((c) => c._id) };
    }
    if (name === 'COUNTRY') {
      return { name, values: [...this.territories] };
    }
    return undefined;
  }

  scopeMissing() {
    const constraint = this.constraint();
    return !!constraint && (constraint.values.length === 0 || constraint.values.length > this.SCOPE_MAX_VALUES);
  }

  isDisabled() {
    return this.submitting || !this.role || this.reasonError.value || this.scopeMissing();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async onConfirm() {
    if (this.isDisabled() || !this.role) {
      return;
    }
    this.submitting = true;
    try {
      // On success the page reloads: nothing else to do here.
      await this.organizationAccessService.enter({
        organizationId: this.organization.id,
        role: this.role,
        reason: this.reason,
        constraint: this.constraint()
      }, this.data.returnUrl);
    } catch (err) {
      this.submitting = false;
      const message = (err as HttpErrorResponse)?.error?.message;
      if (message && this.utils.hasTranslation(`ERRORS.${message}`)) {
        this.toastr.error(`ERRORS.${message}`);
      } else {
        this.toastr.error('ERRORS.GENERIC');
      }
    }
  }

  private async searchCases(query?: string) {
    const request = ++this.casesRequest;
    this.loadingCases = true;
    try {
      const cases = await this.organizationAccessService.searchCases(this.organization.id, query);
      if (request === this.casesRequest) {
        this.cases = cases;
        this.casesLoaded = true;
      }
    } catch {
      if (request === this.casesRequest) {
        this.cases = [];
        this.toastr.error('ERRORS.GENERIC');
      }
    } finally {
      if (request === this.casesRequest) {
        this.loadingCases = false;
      }
    }
  }

  /**
   * The cases of the current scope, with their labels: from the newest cases of the organisation, else read one
   * by one (inside a Case Operator access they are all readable). An unreadable one keeps its id as label.
   */
  private async prefillCases(ids: string[]) {
    await this.searchCases();
    const known = new Map(this.cases.map((c) => [c._id, c]));
    this.selectedCases = await Promise.all(ids.map(async (id): Promise<AccessCase> => {
      const found = known.get(id);
      if (found) {
        return found;
      }
      try {
        const caseObject = await this.caseService.retrieve(id);
        return { _id: id, reference: caseObject.reference, identifier: caseObject.identifier, trademark: caseObject.trademark };
      } catch {
        return { _id: id, reference: id, trademark: { identifierNumber: '', countryOfDesignation: '' } };
      }
    }));
  }
}
